import { typedQuery } from "../../../typed-query";
import {
  IntercomBlogArticle,
  IntercomBlogArticleListItem,
  IntercomBlogAuthorDoc,
  IntercomBlogCategoryDoc,
  IntercomBlogHomepage,
  IntercomBlogSitemapArticle,
  IntercomBlogSitemapTerm,
  IntercomBlogTagDoc,
} from "./types";

/** Default page size for the blog index and its archives. */
export const BLOG_ARTICLES_PER_PAGE = 20;

/** Pass as `perPage` to fetch a whole listing in one response, unpaginated. */
export const ALL_ARTICLES = Number.POSITIVE_INFINITY;

// GROQ needs a finite bound, and this sits far above the ~1,300 posts in WordPress.
const SLICE_CEILING = 100_000;

/** Params for one page of a list, 1-indexed to match `/page/[n]`. */
export function blogArticleRange(
  page: number,
  perPage: number = BLOG_ARTICLES_PER_PAGE,
): { start: number; end: number } {
  if (!Number.isFinite(perPage)) return { start: 0, end: SLICE_CEILING };

  const start = Math.max(0, (Math.max(1, page) - 1) * perPage);
  return { start, end: start + perPage };
}

const OF_TYPE = `_type == "intercomBlogArticle"`;

/**
 * Podcasts and videos share the article document type and are told apart by
 * `format` (WS5-811), so every listing has to say which one it wants or it
 * silently picks up the other two. Documents written before the field existed
 * are articles, hence the coalesce.
 */
const formatIs = (format: "article" | "podcast" | "video") =>
  `coalesce(format, "article") == "${format}"`;

const ARTICLE = `${OF_TYPE} && ${formatIs("article")}`;

/** `_id` breaks date-only ties, which GROQ would otherwise order arbitrarily per page. */
const NEWEST_FIRST = `| order(publishDate desc, _id desc)`;

/** Card fields shared by the index and all three archives. */
const LIST_ITEM = `
  _id,
  title,
  "slug": slug.current,
  "format": coalesce(format, "article"),
  excerpt,
  publishDate,
  theme,
  "authors": authors[defined(@->)]->{
    name,
    "slug": slug.current,
    "avatar": avatar{ "url": asset->url, alt }
  },
  "category": category->{ title, "slug": slug.current },
  "heroImage": heroImage{
    "url": asset->url,
    alt,
    "width": asset->metadata.dimensions.width,
    "height": asset->metadata.dimensions.height
  }
`;

/**
 * The index drops the homepage's featured article so it is not shown twice.
 * Excluding it in GROQ rather than after paging keeps every page full and the
 * total count honest; pass `excludeSlug: null` when nothing is featured.
 */
const INDEX_ARTICLE = `${ARTICLE} && (!defined($excludeSlug) || slug.current != $excludeSlug)`;

/** @returns a slice of every intercomBlogArticle, newest first */
export const intercomBlogArticlesIndexQuery = typedQuery<
  IntercomBlogArticleListItem[]
>(`
*[${INDEX_ARTICLE}] ${NEWEST_FIRST} [$start...$end] {${LIST_ITEM}}`);

/** @returns the total article count, for page-count arithmetic */
export const intercomBlogArticlesCountQuery = typedQuery<number>(
  `count(*[${INDEX_ARTICLE}])`,
);

/** @returns the newest published articles for the RSS feed (WS5-818) — same format filter and order as the index, no featured-article exclusion */
export const intercomBlogFeedArticlesQuery = typedQuery<
  IntercomBlogArticleListItem[]
>(`
*[${ARTICLE}] ${NEWEST_FIRST} [0...$limit] {${LIST_ITEM}}`);

const DETAIL = `
  _id,
  _type,
  "format": coalesce(format, "article"),
  title,
  "slug": slug.current,
  excerpt,
  publishDate,
  modifiedDate,
  theme,
  mediaUrl,
  duration,
  summary,
  "authors": authors[defined(@->)]{
    _key,
    ...@->{
      name,
      "slug": slug.current,
      role,
      twitter,
      linkedin,
      "avatar": avatar{ "url": asset->url, alt }
    }
  },
  "category": category->{ title, "slug": slug.current },
  "tags": tags[defined(@->)]{ _key, ...@->{ title, "slug": slug.current } },
  "heroImage": heroImage{
    "url": asset->url,
    alt,
    "width": asset->metadata.dimensions.width,
    "height": asset->metadata.dimensions.height,
    "mimeType": asset->mimeType
  },
  body[]{
    ...,
    _type == "intercomBlogImageBlock" => {
      ...,
      "image": image{
        "url": asset->url,
        alt,
        caption,
        "width": asset->metadata.dimensions.width,
        "height": asset->metadata.dimensions.height
      }
    }
  },
  seoMetadata{
    metadataTitle,
    metadataDescription,
    "socialImage": socialImage{
      "url": asset->url,
      alt,
      "width": asset->metadata.dimensions.width,
      "height": asset->metadata.dimensions.height,
      "mimeType": asset->mimeType
    },
    canonicalUrl,
    noIndex
  }
`;

const bySlug = (predicate: string) =>
  `*[${predicate} && slug.current == $slug][0] {${DETAIL}}`;

/** @returns one article by slug, taxonomy dereferenced and assets resolved */
export const intercomBlogArticleBySlugQuery = typedQuery<IntercomBlogArticle>(
  bySlug(ARTICLE),
);

/** @returns one podcast episode by slug, for `/blog/podcasts/[slug]` */
export const intercomBlogPodcastBySlugQuery = typedQuery<IntercomBlogArticle>(
  bySlug(`${OF_TYPE} && ${formatIs("podcast")}`),
);

/** @returns one video by slug, for `/blog/videos/[slug]` */
export const intercomBlogVideoBySlugQuery = typedQuery<IntercomBlogArticle>(
  bySlug(`${OF_TYPE} && ${formatIs("video")}`),
);

/**
 * Author, category and tag archives list every format (product decision):
 * unlike the index, these use `OF_TYPE` rather than `ARTICLE`.
 */
const BY_AUTHOR = `${OF_TYPE} && $slug in authors[]->slug.current`;
const BY_CATEGORY = `${OF_TYPE} && category->slug.current == $slug`;
const BY_TAG = `${OF_TYPE} && $slug in tags[]->slug.current`;

/** @returns a slice of an author's posts, newest first */
export const intercomBlogArticlesByAuthorQuery = typedQuery<
  IntercomBlogArticleListItem[]
>(`
*[${BY_AUTHOR}] ${NEWEST_FIRST} [$start...$end] {${LIST_ITEM}}`);

/** @returns how many posts an author has */
export const intercomBlogArticlesByAuthorCountQuery = typedQuery<number>(
  `count(*[${BY_AUTHOR}])`,
);

/** @returns a slice of a category's posts, newest first */
export const intercomBlogArticlesByCategoryQuery = typedQuery<
  IntercomBlogArticleListItem[]
>(`
*[${BY_CATEGORY}] ${NEWEST_FIRST} [$start...$end] {${LIST_ITEM}}`);

/** @returns how many posts a category holds */
export const intercomBlogArticlesByCategoryCountQuery = typedQuery<number>(
  `count(*[${BY_CATEGORY}])`,
);

/** @returns a slice of a tag's posts, newest first */
export const intercomBlogArticlesByTagQuery = typedQuery<
  IntercomBlogArticleListItem[]
>(`
*[${BY_TAG}] ${NEWEST_FIRST} [$start...$end] {${LIST_ITEM}}`);

/** @returns how many posts carry a tag */
export const intercomBlogArticlesByTagCountQuery = typedQuery<number>(
  `count(*[${BY_TAG}])`,
);

const PODCAST = `${OF_TYPE} && ${formatIs("podcast")}`;
const VIDEO = `${OF_TYPE} && ${formatIs("video")}`;

/** @returns a slice of every podcast episode, newest first, for `/blog/podcasts` */
export const intercomBlogPodcastsQuery = typedQuery<
  IntercomBlogArticleListItem[]
>(`
*[${PODCAST}] ${NEWEST_FIRST} [$start...$end] {${LIST_ITEM}}`);

/** @returns how many podcast episodes exist */
export const intercomBlogPodcastsCountQuery = typedQuery<number>(
  `count(*[${PODCAST}])`,
);

/** @returns a slice of every video, newest first, for `/blog/videos` */
export const intercomBlogVideosQuery = typedQuery<
  IntercomBlogArticleListItem[]
>(`
*[${VIDEO}] ${NEWEST_FIRST} [$start...$end] {${LIST_ITEM}}`);

/** @returns how many videos exist */
export const intercomBlogVideosCountQuery = typedQuery<number>(
  `count(*[${VIDEO}])`,
);

/** @returns the author document behind `/blog/author/[slug]` */
export const intercomBlogAuthorBySlugQuery = typedQuery<IntercomBlogAuthorDoc>(`
*[_type == "intercomBlogAuthor" && slug.current == $slug][0] {
  name,
  "slug": slug.current,
  role,
  bio,
  twitter,
  linkedin,
  "avatar": avatar{ "url": asset->url, alt }
}`);

/** @returns the category document behind `/blog/category/[slug]` */
export const intercomBlogCategoryBySlugQuery =
  typedQuery<IntercomBlogCategoryDoc>(`
*[_type == "intercomBlogCategory" && slug.current == $slug][0] {
  title,
  "slug": slug.current,
  description
}`);

/** @returns every category, as the blog's topical entry points (llms.txt) */
export const intercomBlogCategoriesQuery = typedQuery<
  Pick<IntercomBlogCategoryDoc, "title" | "slug">[]
>(`
*[_type == "intercomBlogCategory" && defined(slug.current) && !(_id in path("drafts.**"))] | order(title asc) {
  title,
  "slug": slug.current
}`);

/** @returns the tag document behind `/blog/tag/[slug]` */
export const intercomBlogTagBySlugQuery = typedQuery<IntercomBlogTagDoc>(`
*[_type == "intercomBlogTag" && slug.current == $slug][0] {
  title,
  "slug": slug.current
}`);

/** @returns the blog homepage singleton, with its featured article as a card */
export const intercomBlogHomepageQuery =
  typedQuery<IntercomBlogHomepage | null>(`
*[_type == "intercomBlogHomepage" && _id == "intercomBlogHomepageSingleton"][0]{
  "featuredArticle": select(
    defined(featuredArticle->slug.current) && featuredArticle->slug.current != ""
      && coalesce(featuredArticle->format, "article") == "article"
      => featuredArticle->{${LIST_ITEM}},
    null
  )
}`);

// Excludes drafts, whose ids are prefixed "drafts.", from the sitemap.
const NOT_DRAFT = `!(_id in path("drafts.**"))`;

/** @returns every published article/podcast/video for the sitemap (WS5-817) */
export const intercomBlogSitemapArticlesQuery = typedQuery<
  IntercomBlogSitemapArticle[]
>(`
*[${OF_TYPE} && ${NOT_DRAFT} && defined(slug.current)] {
  _id,
  "slug": slug.current,
  "format": coalesce(format, "article"),
  "updatedAt": _updatedAt,
  "noIndex": seoMetadata.noIndex,
  "canonicalUrl": seoMetadata.canonicalUrl
}`);

/** @returns every category, for the sitemap's page-1 archive entries */
export const intercomBlogSitemapCategoriesQuery = typedQuery<
  IntercomBlogSitemapTerm[]
>(`
*[_type == "intercomBlogCategory" && ${NOT_DRAFT} && defined(slug.current)] { "slug": slug.current }`);
